import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  DialogTitle,
  FormControl,
  IconButton,
  Button,
  Box,
  Dialog,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  Typography,
  Tooltip,
  Radio,
  RadioGroup,
  FormLabel,
  Checkbox,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import SearchableDropdown from './SearchableDropdown';
import FilterPill from './FilterPill';
import _, { filter, set } from 'lodash';
import { Input } from 'reactstrap';
import { getAutoTitle } from '../utilities/plotUtils';
import { updateDecorator } from 'typescript';
import { Search } from 'react-router-dom';

const greaterThanOrEqualSymbol = '\u2265'; // '≥'
const lessThanOrEqualSymbol = '\u2264'; // '≤'

type CustomFilterStepProps = {
  setFilterValues: (filterValues: FilterState) => void;
  categoricalColumns: any;
  numericColumns: any;
  selectedItems?: SelectedItems;
  axisFilterState: FilterState;
  saveAxis?: boolean;
};

const CustomFilterStep: React.FC<CustomFilterStepProps> = ({
  setFilterValues,
  categoricalColumns,
  numericColumns,
  selectedItems,
  axisFilterState,
  saveAxis,
}) => {
    const [modalOpen, setModalOpen] = useState(false);
    const [filterMode, setFilterMode] = useState<'categorical' | 'numeric'>('categorical');
    const [filterBehavior, setFilterBehavior] = useState<'include' | 'exclude'>('include');
    const [filterLevel, setFilterLevel] = useState<'raw' | 'absolute' | 'relative'>('raw');
    const [filterBy, setFilterBy] = useState<SearchableDropdownOption[]>([]);
    const [secondaryFilterBy, setSecondaryFilterBy] = useState<SearchableDropdownOption[]>([]);
    const [secondaryLevelOptions, setSecondaryLevelOptions] = useState<SearchableDropdownOption[]>([]);
    const [filterError, setFilterError] = useState<string | null>(null);

    const [numericMin, setNumericMin] = useState<number | null>(null);
    const [minInput, setMinInput] = useState<string>('');
    const [numericMax, setNumericMax] = useState<number | null>(null);
    const [maxInput, setMaxInput] = useState<string>('');
    const debouncedNumericMin = _.debounce(setNumericMin, 10);
    const debouncedNumericMax = _.debounce(setNumericMax, 10);

    useEffect(() => {
      if (minInput === '') {
        setNumericMin(null);
      } else {
        const parsedMin = parseFloat(minInput);
        if (!isNaN(parsedMin)) {
          setNumericMin(parsedMin);
        }
        else {
          setNumericMin(null);
        }
      }
    }, [minInput]);

    useEffect(() => {
      if (maxInput === '') {
        setNumericMax(null);
      } else {
        const parsedMax = parseFloat(maxInput);
        if (!isNaN(parsedMax)) {
          setNumericMax(parsedMax);
        }
        else {
          setNumericMax(null);
        }
      }
    }, [maxInput]);


    const [editingFilter, setEditingFilter] = useState<Filter | null>(null);
    const [editingFilterIndex, setEditingFilterIndex] = useState<number | null>(null);
    const [isFilterViewable, setIsFilterViewable] = useState(true);
    const [axis, setAxis] = useState<'x' | 'y'>('y');
    const categoricalOptions: SearchableDropdownOption[] = useMemo(() => {
      const options: SearchableDropdownOption[] = [];
      if (categoricalColumns) {
        for (let option in categoricalColumns) {
          if (option.includes('|')){ // filter out treatment, timepoint, sample etc
            let optionLabel = option.replace('|', ' ');
            options.push({ value: option, label: optionLabel });
          }
        }
      }
      return options;
    }, [categoricalColumns]);

    const numericOptions: SearchableDropdownOption[] = useMemo(() => {
      const options: SearchableDropdownOption[] = [];
      if (numericColumns) {
        for (let option in numericColumns) {
          if (option.includes('|')){ // filter out treatment, timepoint, sample etc
            let optionLabel = option.replace('|', ' ');
            options.push({ value: option, label: optionLabel });
          }
        }
        return options;
      }
      return options;
    }, [numericColumns]);

    const relativeColumns = useMemo(() => {
      const relAxes: string[] = [];
      const relTitles: SearchableDropdownOption[] = [];
      // console.log('selectedItems in relativeColumns:', selectedItems);
      if (selectedItems) {
        for (let axis of ['Y-Axis Data', 'X-Axis Data', 'Z-Axis Data']) {
          if (selectedItems[axis] && selectedItems[axis][0] && selectedItems[axis][0].method === 'data' && (selectedItems[axis][0]?.references?.length ?? -1 > 0) ) {
            relAxes.push(axis);
          }
        }
        for (let axis of relAxes) {
          console.log('Relative Column Detected on :', axis);
          const colValue = selectedItems[axis][0].values[0];
          const vt = numericOptions.find((col: SearchableDropdownOption) => col.value.includes(`|${colValue}`))?.value.split('|')[0];
          const option = {label: getAutoTitle([{...selectedItems[axis][0], processingMode: 'relative'}]), value: `${vt}|${colValue}`}; 
          // console.log("Option:", option);
          relTitles.push(option);

        }
      }
      return relTitles;
    }, [selectedItems, numericOptions]);

    

  const combinedOptions: SearchableDropdownOption[] = useMemo(() => {
    return [...relativeColumns, ...numericOptions, ...categoricalOptions,];
  }, [categoricalOptions, numericOptions, relativeColumns]);
  
  useEffect(() => {
    // console.log('filterBy changed:', filterBy);
    // console.log('categoricalColumns:', categoricalColumns);
    // console.log('numericColumns:', numericColumns);
    if (filterBy.length === 0) {
      return;
    }
    // check if column is already filtered
    const filteredColumns = Object.values(axisFilterState).map((filter) => `${filter.versionType}|${filter.column}`);
    const matchingIndex = filteredColumns.indexOf(filterBy[0].value);
    // console.log(matchingIndex, editingFilterIndex, filteredColumns, filterBy[0].value, editingFilterIndex !== matchingIndex);
    if (filteredColumns.includes(filterBy[0].value) && matchingIndex !== -1 && editingFilterIndex !== matchingIndex){
      setFilterError('This column is already filtered. Please select another column.');
    } 
    else {
      setFilterError(null);
    }

    if (filterBy[0].value in categoricalColumns) {
      setFilterMode('categorical');
    } else if (filterBy[0].value in numericColumns) {
      setFilterMode('numeric');

      if (relativeColumns.map(col => col.label).includes(filterBy[0].label)) {
        setFilterLevel('relative');
      }
      else if (filterLevel !== 'raw') {
        setFilterLevel('absolute');
      }
    }

  }, [filterBy, categoricalColumns, numericColumns, relativeColumns, filterLevel]);

  useEffect(() => {
    if (filterLevel !== 'raw' && filterMode === 'numeric') {
      if (relativeColumns.map(col => col.label).includes(filterBy[0].label) && filterLevel !== 'relative') {
        setFilterLevel('relative');
      }
      else if (filterLevel !== 'absolute' && !relativeColumns.map(col => col.label).includes(filterBy[0].label)) {
        setFilterLevel('absolute');
      }
    } 
  }, [filterLevel]);

  // useEffect(() => {
  //   if (filterMode === 'categorical') {
  //     setNumericMin(null);
  //     setNumericMax(null);
  //   }
  //   else if (filterMode === 'numeric') {
  //     setSecondaryFilterBy([]);
  //   }
  //   setFilterBy([]);
  // }, [filterMode]);
    
  const handleNewModalOpen = () => {
    setFilterBy([]);
    setFilterBehavior('include');
    setNumericMin(null);
    setNumericMax(null);
    setMinInput('');
    setMaxInput('');
    setFilterLevel('absolute');
    setFilterMode('categorical');
    setEditingFilter(null);
    setEditingFilterIndex(null);
    setIsFilterViewable(true);
    setSecondaryFilterBy([]);
    setModalOpen(true);
  };

  const handleOpen = (filter: Filter, index: number) => {
    // const [filterByValue, secondaryFilterByType, secondaryFilterByValues] = filter.value.split('|');
    const filterVersionType = filter.versionType;
    const filterColumn = filter.column;
    let joinedValue = `${filterVersionType}|${filterColumn}`;
    
    if (filter.type === 'numeric') {
      setFilterLevel(filter.level)
    }
    else {
      setFilterLevel('raw')
    }
    setTimeout(() => {}, 0); // to allow filterMode to update before setting filterBy
    setFilterBehavior(filter.filterBehavior);

    setIsFilterViewable(filter.viewable);
    if (filter.type === 'categorical' && filter.value) {
      let splitSecondary = filter.value as SearchableDropdownOption[];
      setSecondaryFilterBy(splitSecondary);
    }
    else if (filter.type === 'numeric' && filter.value) {
      let numericRange = (filter as NumericFilter).value as [ number | null, number | null ];
      setNumericMin(numericRange[0]);
      setNumericMax(numericRange[1]);
      setMinInput(numericRange[0] !== null ? numericRange[0].toString() : '');
      setMaxInput(numericRange[1] !== null ? numericRange[1].toString() : '');
    }

    const filterByOption = combinedOptions.find((option) => `${option.value.split('|')[0]}|${option.value.split('|')[1]}` === joinedValue);
    setFilterBy(filterByOption ? [filterByOption] : []);
    
    if (filterByOption) {
  
      // Update the secondaryLevelOptions based on the selected filterBy value
      const filterKey = filterByOption.value;
      const secondaryOptions = categoricalColumns[filterKey] || [];
      console.log('secondaryOptions', secondaryOptions);
      const updatedSecondaryOptions = secondaryOptions.map((option: string) => ({
        value: option,
        label: option,
      }));
      setSecondaryLevelOptions(updatedSecondaryOptions);
    }
  
    setEditingFilter(filter);
    setEditingFilterIndex(index);
    setModalOpen(true);
  };

  const handleFilterViewableToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsFilterViewable(event.target.checked);
  };

  const handleClose = () => {
    setModalOpen(false);
  };

  const handleSaveFilter = useCallback(() => {
    setModalOpen(false);
    console.log("Saving Filter");
    console.log("Filter by", filterBy);
    console.log("filterMode", filterMode);

    if (filterBy.length > 0 && (secondaryFilterBy.length > 0 || filterMode === 'numeric')) {
      // if (filterMode === 'numeric' && (numericMin === null && numericMax === null)) {
      //   // If both min and max are null, do not save the filter
      //   return;
      // }
      console.log("Value to save", filterBehavior)
      const updatedFilter: Filter = 
        filterMode === 'categorical' ? {
          value: secondaryFilterBy,
          viewable: isFilterViewable,
          filterBehavior: filterBehavior,
          type: filterMode,
          versionType: filterBy[0].value.split('|')[0],
          column: filterBy[0].value.split('|')[1],
          axis: saveAxis ? axis : undefined
        }: {
          value: [numericMin, numericMax],
          viewable: isFilterViewable,
          filterBehavior: filterBehavior,
          boundaries: 'inclusive',
          level: filterLevel,
          type: filterMode,
          versionType: filterBy[0].value.split('|')[0],
          column: filterBy[0].value.split('|')[1],
          axis: saveAxis ? axis : undefined
        }

      if (editingFilter !== null && editingFilterIndex !== null) {
        // If editing an existing filter, update it in the filter values
        const updatedFilterValue = structuredClone(axisFilterState);
        updatedFilterValue[editingFilterIndex] = updatedFilter;
        setFilterValues(updatedFilterValue);
      } else {
        // If adding a new filter, check for duplicates before adding
        const filterExists = Object.values(axisFilterState).some((filter) => filter.column === updatedFilter.column && filter.versionType === updatedFilter.versionType && (filter.type === 'categorical' || (filter.type === 'numeric' && updatedFilter.type === 'numeric' && filter.level === updatedFilter.level)));
        if (!filterExists) {
          const updatedAxisFilters = structuredClone(axisFilterState);
          const newIndex = Object.keys(updatedAxisFilters).length;
          updatedAxisFilters[newIndex] = updatedFilter;
          setFilterValues(updatedAxisFilters);
          // setFilterValues([...filterValue, updatedFilter]);
        }
        else {
          // if a filter for this column already exists, replace that filter
          // If implimenting axis, need to check if axis is the same too
          const updatedFilterValue = structuredClone(axisFilterState);
          for (let key in updatedFilterValue) {
            const oldFilter = updatedFilterValue[key];
            if (updatedFilter.column === oldFilter.column && updatedFilter.versionType === oldFilter.versionType &&  (updatedFilter.type === 'categorical' || (updatedFilter.type === 'numeric' && oldFilter.type === 'numeric' && updatedFilter.level === oldFilter.level))) {
              updatedFilterValue[key] = updatedFilter;
              break;
            }
          }
          setFilterValues(updatedFilterValue);
        }
      }

    }
  
  }, [filterBy, secondaryFilterBy, filterMode, numericMin, numericMax, isFilterViewable, filterBehavior, filterLevel, editingFilter, editingFilterIndex, axisFilterState, setFilterValues, saveAxis, axis]);

  const handleRemoveFilter = (filterToRemove: number) => {
    const updatedFilters = structuredClone(axisFilterState);
    delete updatedFilters[filterToRemove];
    setFilterValues(updatedFilters);
  };

  useEffect(() => {
    if (filterBy.length === 0) {
      setSecondaryLevelOptions([]);
      return;
    }
    const filterKey = filterBy[0].value;
    const secondaryOptions = categoricalColumns[filterKey] || [];
    const updatedSecondaryOptions = secondaryOptions.map((option: string) => ({
      value: option,
      label: option,
    }));
    setSecondaryLevelOptions(updatedSecondaryOptions);
  }, [filterBy, categoricalColumns]);

  const style = {
    marginLeft: 0,
    marginTop: 1,
    marginBottom: 0,
    '& .MuiSwitch-root': {
      padding: 1,
    },
    '& .MuiFormControlLabel-label': {
      marginRight: '8px',
      fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
      fontSize: '.9rem',
      fontWeight: 400,
      lineHeight: 1.5,
      color: 'rgba(0, 0, 0, 0.6)',
    },
  }

  return (
    <div>
      {/* <ListItem sx={{ justifyContent: 'flex-start', paddingBottom: 0, marginBottom: 0 }}>
        <ListItemText sx={{ paddingBottom: 0, marginBottom: 0 }} secondary="Custom Filter:" />
        <IconButton onClick={handleNewModalOpen}>
          <FilterAltIcon />
        </IconButton>
      </ListItem> */}
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 1 }}>
    <DialogTitle sx={{ 'paddingLeft': '15px', 'paddingTop': '15px' }}>
    <u>Custom Filters</u>
    </DialogTitle>
    <Tooltip title="Add Filter">
      <IconButton
        color="default"
        onClick={handleNewModalOpen}
        sx={{ 
          color: 'grey', 
          marginLeft: '-1px', 
          '& .MuiSvgIcon-root': { fontSize: '22px' }, // Adjust icon size
        }}
      >
        <AddCircleOutlineIcon />
      </IconButton>
    </Tooltip>
  </Box>
    <Box sx={{ display: 'flex', flexWrap: 'wrap', marginBottom: 2, p: 1, borderRadius: 0, marginLeft: '5px' }}>
      {Object.values(axisFilterState).map((filter, index) => (
        <FilterPill
          key={index}
          label={filter.column + (filter.type === 'numeric' ? ` (${filter.level})` : '')}
          onDelete={() => handleRemoveFilter(index)}
          onClick={() => handleOpen(filter, index)}
        />
      ))}
    </Box>
      <Dialog
        open={modalOpen}
        onClose={handleClose}
        fullWidth
      >
        <DialogTitle>{editingFilter ? 'Edit Filter' : 'Add Filter'}</DialogTitle>
        <DialogContent>
          {/* <FormControl>
            <FormLabel id="filter-mode-label" >Filter Mode</FormLabel>
              <RadioGroup
                row
                aria-labelledby="filter-mode-label"
                value={filterMode}
                onChange={(e) => setFilterMode(e.target.value as 'categorical' | 'numeric')}
              >
                <FormControlLabel value="categorical" control={<Radio size='small' />} label="Categorical" />
                <FormControlLabel value="numeric" control={<Radio size='small' />} label="Numeric" />
              </RadioGroup>
          </FormControl> */}
          <FormControl fullWidth margin="normal" style={{ marginBottom: '15px' }}>
            <SearchableDropdown
              multiple={false}
              key={'filterBy'}
              label={'Filter By'}
              value={filterBy}
              setValue={setFilterBy}
              options={combinedOptions}
              placeholder="Select a Column to Filter"
            />
          </FormControl>
          {/* {filterMode === 'numeric' && filterLevel !== 'raw' && 
            <Typography variant="body2" color="textSecondary" style={{ marginTop: '10px', marginBottom: '10px' }}>
              Numeric filters in Absolute or Relative mode apply to whatever data is plotted
            </Typography>
          } */}
          {filterBy.length > 0 && filterMode === 'categorical' && <SearchableDropdown
            multiple={true}
            key={'filterBySecondary'}
            label={'Filter Values'}
            value={secondaryFilterBy}
            setValue={setSecondaryFilterBy}
            options={secondaryLevelOptions}
            placeholder={`Values to ${filterBehavior === 'include' ? 'Include' : 'Exclude'}`}
          />}
          {filterMode === 'numeric' && (filterBy.length > 0 && filterLevel === 'raw' || filterLevel !== 'raw') && <>
            <Box flexDirection={'row'} display='flex'>
              {/* <Typography variant="body2" color="textSecondary" style={{ marginTop: '17px', marginBottom: '10px', marginRight: '10px' }}><b>{(filterBehavior === 'include') ? 'Show' : 'Hide'}</b> values within the range:</Typography> */}
              <Input type="number" placeholder="Min Value" value={minInput} style={{ marginRight: '10px', marginTop: '15px', maxWidth: '120px', height: '50px'}} onChange={(value) => setMinInput(value.currentTarget.value)} />
              <Typography variant="h6" align="center" sx={{ marginTop: '20px', marginRight: '10px', marginLeft: '0px', color:'#808080'}}> {filterBehavior === 'include' ? '≤ X ≤' : '≥ X ≥'} </Typography>
              <Input type="number" placeholder="Max Value" value={maxInput} style={{ marginTop: '15px' , maxWidth: '120px', height: '50px'}}  onChange={(value) => setMaxInput(value.currentTarget.value)} />
              <FormControl style={{ marginLeft: '15px', marginTop: '17px' }}>
                {/* <FormLabel id="filter-behavior-label" >Filter Behavior</FormLabel> */}
                <RadioGroup
                  row
                  // aria-labelledby="filter-behavior-label"
                  value={filterBehavior}
                  
                  onChange={(e) => setFilterBehavior(e.target.value as 'include' | 'exclude')}
                >
                  <FormControlLabel value="include" control={<Radio size='small' />} label="Include" />
                  <FormControlLabel value="exclude" control={<Radio size='small' />} label="Exclude" />
                </RadioGroup>
              </FormControl>
            </Box>
            </>
          }
          <br></br>
          {filterMode === 'categorical' && <FormControl style={{ marginTop: '10px' }}>
            {/* <FormLabel id="filter-behavior-label" >Filter Behavior</FormLabel> */}
            <RadioGroup
              row
              value={filterBehavior}
              onChange={(e) => setFilterBehavior(e.target.value as 'include' | 'exclude')}
            >
              <FormControlLabel value="include" control={<Radio size='small' />} label="Include" />
              <FormControlLabel value="exclude" control={<Radio size='small' />} label="Exclude" />
            </RadioGroup>
          </FormControl>}
          {/* <br></br> */}
          {filterMode === 'numeric' &&
            <Tooltip title="Apply filter to raw data, before data processing occurs.">
              <FormControlLabel control={<Checkbox disabled={filterLevel === 'relative'} checked={filterLevel === 'raw'} onChange={(e) => setFilterLevel(filterLevel === 'raw' ? 'absolute' : 'raw')}/>} label="Apply before data pipeline?" />
              </Tooltip>
          }
          {filterError !== null && <Typography variant="body2" color="error" style={{ marginTop: '5px' }}>{filterError}</Typography>}
          {/* <br></br> */}
          {/* <FormControlLabel
            control={
              <Switch
                checked={isFilterViewable}
                onChange={handleFilterViewableToggle}
                name="filterViewableToggle"
                color="primary"
                size="small"
              />
            }
            label="Make Filter Viewable"
            labelPlacement="start"
            sx={style}
          /> */}
          {saveAxis && <FormControl>
            <FormLabel id="filterAxis"></FormLabel>
            <RadioGroup
              aria-labelledby="filterAxis"
              defaultValue="female"
              row
              name="radio-buttons-group"
              value={axis}
              // labelPlacement='start'
              onChange={(event)=>setAxis(event.target.value as any)}
            >
              <FormControlLabel sx={style} value="x" control={<Radio size='small' />} label="X Axis" />
              <FormControlLabel sx={style} value="y" control={<Radio size='small'/>} label="Y Axis" />
            </RadioGroup>
          </FormControl>}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveFilter} disabled={filterError !== null}>
            {editingFilter ? 'Save' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CustomFilterStep;